// Log events carry the page URL; Auth0 callbacks put one-time `code`/`state` in the query, so only origin + path may be sent.
const toOriginAndPath = (value: string): string => {
  try {
    const url = new URL(value);
    return url.origin + url.pathname;
  } catch {
    return '';
  }
};

// Mutates and returns the event; must never return false because that would drop the log.
export const trimLogUrls = (log: { view?: { url?: unknown; referrer?: unknown } }): boolean => {
  try {
    const view = log?.view;
    if (view) {
      if (typeof view.url === 'string') view.url = toOriginAndPath(view.url);
      if (typeof view.referrer === 'string') view.referrer = toOriginAndPath(view.referrer);
    }
  } catch {
    // Logging must never affect the app.
  }
  return true;
};

const REVIEW_APP_HOST_RE = /^review-(\d+)-picwell-app\.jellydevs\.com$/;

// Matches the env names picwell-app's server logs use, so browser and server logs filter together.
// Review apps are named per MR, matching their server logs; their deploy environment says staging.
export const datadogEnv = (deployEnv: unknown, hostname: string): unknown => {
  const reviewMatch = REVIEW_APP_HOST_RE.exec(hostname);
  if (reviewMatch) return `review-${reviewMatch[1]}`;
  return deployEnv === 'prod' ? 'production' : deployEnv;
};

// The logs script loads asynchronously (see index.html), so every call waits for it: onReady runs
// right away once it has loaded, and queues until then. A missing script means nothing is sent.
const whenLogsReady = (callback: () => void): void => {
  window.DD_LOGS?.onReady(callback);
};

// Only explicit logInfo calls are sent: errors, console output and reports are not forwarded.
export const initDatadogLogs = ({ clientToken, env }: { clientToken: string; env: unknown }): void => {
  try {
    whenLogsReady(() => {
      try {
        window.DD_LOGS?.init({
          clientToken,
          site: 'us3.datadoghq.com',
          service: 'picwell-app',
          env,
          forwardErrorsToLogs: false,
          forwardConsoleLogs: [],
          forwardReports: [],
          sessionSampleRate: 100,
          beforeSend: trimLogUrls,
        });
        window.DD_LOGS?.setGlobalContextProperty('environment', env);
      } catch (error) {
        console.error('Failed to initialize Datadog Logs:', error);
      }
    });
  } catch {
    // Logging must never affect the app.
  }
};

// Safe to call from any code path: does nothing when the Datadog script is absent and never throws.
export const logInfo = (event: string, fields: Record<string, unknown> = {}): void => {
  try {
    whenLogsReady(() => {
      try {
        window.DD_LOGS?.logger?.info(event, { event, ...fields });
      } catch {
        // Logging must never affect the app.
      }
    });
  } catch {
    // Logging must never affect the app.
  }
};
